import { useState } from "react";
import { useTranslations } from "next-intl";
import {
  useRoleTemplateScopeSources,
  type ScopeItem,
} from "./useRoleTemplateScopeSources";

/**
 * Slotting resource-visibility allowlists for a CUSTOM role template. Each is a
 * list of ObjectId hex strings; an empty/absent list = no restriction (the role
 * sees every resource of that type). Enforced server-side, narrow-only,
 * fail-closed by `SlottingScopeEnforcer`. Hidden offerings/treatments/lanes also
 * hide their appointments/requests. Mirrors the check-in device item-allowlists.
 */
export type SlottingScopeAllowlists = {
  visibleOfferingIds?: string[];
  visibleTreatmentIds?: string[];
  visibleLaneIds?: string[];
};

/**
 * One collapsible "limit to specific items" multi-select. Empty selection = no
 * restriction (all items visible). Chips toggle ObjectId hex ids into the list.
 */
function ScopeMultiSelect({
  title,
  hint,
  options,
  selected,
  disabled,
  emptyLabel,
  allLabel,
  onChange,
}: {
  title: string;
  hint: string;
  options: ScopeItem[];
  selected: string[];
  disabled?: boolean;
  emptyLabel: string;
  allLabel: string;
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(selected.length > 0);
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const restricted = selected.length > 0;
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border outline-quaternary surface-secondary p-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between gap-2 text-left"
      >
        <span className="body-3-medium font-secondary">{title}</span>
        <span
          className={`shrink-0 px-1.5 py-0.5 rounded-md text-[11px] ${
            restricted ? "chip-green-bg chip-green-fg" : "chip-grey-bg chip-grey-fg"
          }`}
        >
          {restricted ? `${selected.length}` : allLabel}
        </span>
      </button>
      {open && (
        <>
          <span className="body-3-regular font-quaternary">{hint}</span>
          {options.length === 0 ? (
            <span className="body-3-regular font-quaternary py-1">{emptyLabel}</span>
          ) : (
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {options.map((opt) => {
                const on = selected.includes(opt.id);
                return (
                  <button
                    type="button"
                    key={opt.id}
                    aria-pressed={on}
                    disabled={disabled}
                    onClick={() => toggle(opt.id)}
                    className={`px-2 py-0.5 rounded-md text-xs border ${
                      on
                        ? "chip-green-bg chip-green-fg"
                        : "outline-quaternary font-tertiary hover:surface-tertiary"
                    } ${disabled ? "opacity-50 cursor-not-allowed" : "transition-colors"}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The three slotting visibility allowlists (offerings / treatments / lanes) for
 * a role template. Loads its own option lists from the event. Empty = no
 * restriction.
 */
export function RoleTemplateScopePickers({
  eventId,
  scopes,
  onChange,
  disabled,
}: {
  eventId: string | undefined;
  scopes: SlottingScopeAllowlists;
  onChange: (next: SlottingScopeAllowlists) => void;
  disabled?: boolean;
}) {
  const t = useTranslations("roleTemplates");
  const sources = useRoleTemplateScopeSources(eventId);
  const patch = (p: Partial<SlottingScopeAllowlists>) => onChange({ ...scopes, ...p });

  return (
    <div className="flex flex-col gap-2">
      <span className="body-3-regular font-tertiary">{t("slottingScopeHint")}</span>
      <ScopeMultiSelect
        title={t("slottingOfferings")}
        hint={t("slottingOfferingsHint")}
        options={sources.offerings}
        selected={scopes.visibleOfferingIds ?? []}
        disabled={disabled}
        emptyLabel={t("slottingNoOfferings")}
        allLabel={t("slottingAll")}
        onChange={(next) => patch({ visibleOfferingIds: next })}
      />
      <ScopeMultiSelect
        title={t("slottingTreatments")}
        hint={t("slottingTreatmentsHint")}
        options={sources.treatments}
        selected={scopes.visibleTreatmentIds ?? []}
        disabled={disabled}
        emptyLabel={t("slottingNoTreatments")}
        allLabel={t("slottingAll")}
        onChange={(next) => patch({ visibleTreatmentIds: next })}
      />
      <ScopeMultiSelect
        title={t("slottingLanes")}
        hint={t("slottingLanesHint")}
        options={sources.lanes}
        selected={scopes.visibleLaneIds ?? []}
        disabled={disabled}
        emptyLabel={t("slottingNoLanes")}
        allLabel={t("slottingAll")}
        onChange={(next) => patch({ visibleLaneIds: next })}
      />
    </div>
  );
}
